import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Output,
  SimpleChanges,
} from '@angular/core';
import { AbstractControl, FormControl, FormGroup } from '@angular/forms';

import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { User } from '@recruitee/http/types';

import { RtStatefulComponent } from '../../utils/rt-stateful-component';

// TODO translations got wrong namespace app, probably should be moved into rtu namespace, so full component could be reused

type State = {
  assignmentsGroupValue: any;
  searchControlValue: string;
  selectedAdminsCount: number;
};

@Component({
  selector: 'rt-admins-select-dialog-form',
  templateUrl: './admins-select-dialog-form.component.html',
  styleUrls: ['./admins-select-dialog-form.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdminsSelectDialogFormComponent extends RtStatefulComponent<State> {
  @Input() public heading: string;
  @Input() public subheading: string;
  @Input() public assignments: number[] = [];
  @Input() public admins: User[] = [];
  @Input() public currentAdminId: number | null;
  @Input() public loading: boolean = false;
  @Input() public loadingAdmins: boolean = false;
  @Input() public preventAdminRemove: boolean = false;
  @Input() public showCountBeforeSubheading: boolean = false;
  @Input() public minSelectedAdmins: number = 0;
  @Input() public maxSelectedAdmins: number = 0;

  @Output() public cancelClick: EventEmitter<any> = new EventEmitter();
  @Output() public submitClick: EventEmitter<any> = new EventEmitter();

  public searchControl: FormControl = new FormControl('');
  public assignmentsGroup: FormGroup = new FormGroup({});

  private ngOnDestroy$: Subject<void> = new Subject();

  constructor(protected cdRef: ChangeDetectorRef) {
    super(cdRef);
    this.createState({
      assignmentsGroupValue: null,
      searchControlValue: '',
      selectedAdminsCount: this.assignments.length,
    });
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (
      changes.assignments &&
      changes.assignments.currentValue &&
      !changes.assignments.previousValue
    ) {
      this.setState({
        ...this.state,
        selectedAdminsCount: changes.assignments.currentValue.length,
      });
    }
  }

  public ngOnInit(): void {
    const destroy$ = this.ngOnDestroy$.asObservable();

    this.assignmentsGroup.valueChanges.pipe(takeUntil(destroy$)).subscribe((formValue: any) => {
      this.setState({
        ...this.state,
        assignmentsGroupValue: formValue,
        selectedAdminsCount: this.getSelectedCount(),
      });
    });

    this.searchControl.valueChanges
      .pipe(takeUntil(destroy$))
      .subscribe((searchControlValue: string) => {
        this.setState({ ...this.state, searchControlValue });
      });
  }

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public controlFor(admin: any): AbstractControl {
    if (!this.assignmentsGroup.controls[admin.id]) {
      const control = new FormControl(this.assignments.indexOf(admin.id) < 0 ? false : admin.id);
      this.assignmentsGroup.registerControl(admin.id, control);
    }
    return this.assignmentsGroup.controls[admin.id];
  }

  public selectAll(): any {
    this.assignmentsGroup.setValue(
      this.admins.reduce((object: { [key: number]: number }, admin) => {
        object[admin.id] = admin.id;
        return object;
      }, {}),
    );
  }

  public selectNone(): void {
    this.assignmentsGroup.reset();
    if (this.preventAdminRemove) {
      this.assignmentsGroup.get(this.currentAdminId!.toString())!.setValue(this.currentAdminId);
    }
  }

  public toggleAssignment(admin: any): void {
    const control = this.assignmentsGroup.controls[admin.id];
    control.setValue(control.value ? false : admin.id);
  }

  public getSelectedCount(): number {
    return Object.values(this.assignmentsGroup.value).filter(v => v).length;
  }

  public isSearching(): boolean {
    return this.searchControl.value && this.searchControl.value.length > 0;
  }

  public cancelSearch(): void {
    this.searchControl.reset('');
  }

  public cancel(): void {
    this.cancelClick.emit();
  }

  public isValid(): boolean {
    if (this.preventAdminRemove) {
      const selectedAdmins = this.admins.filter(admin => this.assignmentsGroup.value[admin.id]);
      if (!selectedAdmins.map(admin => admin.id).includes(<number>this.currentAdminId)) {
        return false;
      }
    }
    if (this.minSelectedAdmins && this.getSelectedCount() < this.minSelectedAdmins) {
      return false;
    }
    if (this.maxSelectedAdmins && this.getSelectedCount() > this.maxSelectedAdmins) {
      return false;
    }

    return true;
  }

  public submit(): void {
    const selectedAdmins = this.admins.filter(admin => this.assignmentsGroup.value[admin.id]);
    this.submitClick.emit(selectedAdmins);
  }

  public trackByAdmin(index: number, admin: any): number {
    return admin.id;
  }
}
